/* Console · Client mode (v1) — the demo runner INSIDE the product.
 *
 * Ale's rulings (2026-11-01): overlay on the real app, behind the login,
 * sidebar entry, officer always in control. The host owns three surfaces:
 *
 *   · the rail entry      (rendered by V2Shell via ConsoleRailEntry)
 *   · the client panel    (pick a story · reset the record)
 *   · the runner bar      (slim bottom overlay: story, step, narration,
 *                          pause / next, pace)
 *
 * Pace modes: "wait for me" and "auto" ship in v1. "Drive for me" — the
 * console operating the UI itself, officer able to take back control —
 * is v2 (Ale directive): the seam is `ConsoleDriver` below. A driver
 * receives each step after its document lands and may navigate/act
 * before ceding; v1 registers none and the option renders disabled.
 * The approved paced auto-officer (`nolos-console-officer/auto_officer.py`,
 * outside the repo) is the reference for that v2 driver.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '../../contexts/useAuth'
import {
  fetchStoryDocument,
  fetchSigning,
  consoleErrorMessage,
  transientConsoleRead,
  signStory,
  isReadySetup,
  fetchResetPreview,
  type SigningStatus,
  type ReadySetup,
  type ResetPreview,
  resetRecord,
  uploadStoryDocument,
  deliverStoryToFolder,
  type DeliveryMode,
  useConsole,
  usePendingStoryUploads,
  useResetPending,
  type Story,
  type StoriesResponse,
} from './api'
import RequestsPanel from './RequestsPanel'
import './console.css'

export type PaceMode = 'wait' | 'auto' | 'drive'

/** v2 seam — see the header comment. */
export interface ConsoleDriver {
  onStepLanded(storyId: string, seq: number): Promise<void>
}
/** v1 ships no driver; a v2 auto-officer registers here. */
export const consoleDriver: ConsoleDriver | null = null

/** Seconds the auto pace dwells on a landed step before feeding the next
 *  document — long enough to watch the pipeline pick it up. */
const AUTO_DWELL_S = 25

interface RunState {
  story: Story
  stepIx: number // index into story.steps of the CURRENT step
  phase: 'signing' | 'feeding' | 'landed' | 'failed' | 'done'
  setup: ReadySetup | null
  delivery: DeliveryMode
  note: string | null
  paused: boolean
  countdown: number | null
}

interface ConsoleCtx {
  enabled: boolean | null
  open: () => void
}
const Ctx = createContext<ConsoleCtx>({ enabled: false, open: () => {} })
// eslint-disable-next-line react-refresh/only-export-components
export const useConsoleHost = () => useContext(Ctx)

export default function ConsoleHost({ children }: { children: ReactNode }) {
  const { enabled, catalogue } = useConsole()
  const [panel, setPanel] = useState(false)
  const open = useCallback(() => setPanel(true), [])
  return (
    <Ctx.Provider value={{ enabled, open }}>
      {children}
      {panel && catalogue ? (
        /* The overlays live OUTSIDE the .v2 flex shell (fixed-position),
           but the v2 token aliases are scoped to .v2 — this wrapper
           carries the scope without adding a box (display: contents). */
        <div className="v2 console-layer">
          <ConsoleSurfaces catalogue={catalogue} onClose={() => setPanel(false)} />
        </div>
      ) : null}
    </Ctx.Provider>
  )
}

/** The rail entry — V2Shell renders this at the bottom of the sidebar. */
export function ConsoleRailEntry() {
  const { enabled, open } = useConsoleHost()
  if (enabled !== true) return null
  return (
    <button
      type="button"
      className="console-rail-entry"
      onClick={open}
      data-testid="console-rail-entry"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 5h14v10H5z M8 19h8 M12 15v4" />
      </svg>
      <span>
        Console <small>· Client mode</small>
      </span>
    </button>
  )
}

function ConsoleSurfaces({
  catalogue,
  onClose,
}: {
  catalogue: StoriesResponse
  onClose: () => void
}) {
  const [run, setRun] = useState<RunState | null>(null)
  const [requestsStory, setRequestsStory] = useState<Story | null>(null)
  if (requestsStory)
    return (
      <RequestsPanel
        key={requestsStory.id}
        story={requestsStory}
        onClose={onClose}
        onBack={() => setRequestsStory(null)}
      />
    )
  if (run?.phase === 'signing')
    return (
      <SigningBeat
        story={run.story}
        onEnd={() => setRun(null)}
        onReady={(setup, delivery) =>
          setRun((current) =>
            current ? { ...current, setup, delivery, phase: 'feeding' } : current
          )
        }
      />
    )
  if (run)
    return (
      <RunnerBar
        run={run}
        setRun={setRun}
        onEnd={() => {
          setRun(null)
        }}
      />
    )
  return (
    <ClientPanel
      catalogue={catalogue}
      onClose={onClose}
      onRequests={setRequestsStory}
      onStart={(story) =>
        setRun({
          story,
          stepIx: 0,
          phase: 'signing',
          setup: null,
          delivery: 'browser',
          note: null,
          paused: false,
          countdown: null,
        })
      }
    />
  )
}

/* ── the client panel ─────────────────────────────────────────────── */

function ClientPanel({
  catalogue,
  onClose,
  onStart,
  onRequests,
}: {
  catalogue: StoriesResponse
  onClose: () => void
  onStart: (story: Story) => void
  onRequests: (story: Story) => void
}) {
  const { user } = useAuth()
  const admin = user?.role === 'admin'
  const [resetting, setResetting] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [preview, setPreview] = useState<ResetPreview | null>(null)
  const [previewVersion, setPreviewVersion] = useState(0)
  useEffect(() => {
    if (!resetting) return
    let active = true
    setPreview(null)
    setTyped('')
    fetchResetPreview()
      .then((value) => {
        if (active) setPreview(value)
      })
      .catch((error) => {
        if (active) setOutcome(consoleErrorMessage(error))
      })
    return () => {
      active = false
    }
  }, [resetting, previewVersion])
  // A story upload the server has not confirmed settled could still land a
  // document AFTER a wipe — reset stays locked until the count drains.
  const settling = usePendingStoryUploads() > 0
  // And the other direction (Codex, second pass): while a wipe runs on the
  // server, no story may start feeding documents into it. Module-scoped so
  // closing and reopening the panel cannot forget a running reset.
  const wiping = useResetPending()

  const doReset = async () => {
    setBusy(true)
    setOutcome(null)
    try {
      if (!preview?.can_reset) return
      const r = await resetRecord(typed, preview.digest)
      setOutcome(r.summary)
      setResetting(false)
      setTyped('')
    } catch (e) {
      setOutcome(consoleErrorMessage(e))
      setTyped('')
      setPreviewVersion((value) => value + 1)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="console-scrim" onClick={onClose} />
      <aside className="console-panel" role="dialog" aria-label="Console · Client mode">
        <div className="cp-head">
          <span className="cp-title">Console</span>
          <span className="cp-mode">Client mode</span>
          <button type="button" className="cp-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="cp-sub">
          Runs a story through the product itself — documents feed the normal upload path, the
          pipeline does its real work, and you stay in control throughout.
        </p>
        <div className="cp-stories">
          {catalogue.stories.map((s) => (
            <div className="cp-story" key={s.id} data-testid={`console-story-${s.id}`}>
              <div className="cp-story-name">{s.name}</div>
              <div className="cp-story-tag">{s.tagline}</div>
              <div className="cp-story-meta">
                {s.steps.length} documents
                {s.steps.some((step) => step.day != null) ? (
                  <>
                    {' '}
                    ·{' '}
                    {
                      new Set(s.steps.flatMap((step) => (step.day == null ? [] : [step.day]))).size
                    }{' '}
                    delivery days
                  </>
                ) : null}
              </div>
              <button
                type="button"
                className="vbtn pri"
                disabled={wiping}
                onClick={() => onStart(s)}
              >
                {wiping ? 'Reset in progress…' : 'Start story ›'}
              </button>
              <button
                type="button"
                className="vbtn"
                disabled={wiping}
                onClick={() => onRequests(s)}
              >
                Requests
              </button>
            </div>
          ))}
        </div>
        {admin ? (
          <div className="cp-reset">
            <div className="cp-reset-head">Reset record</div>
            <p className="cp-reset-sub">
              Clears resettable funds, documents and tasks, and disconnects this demo’s linked
              folders. Logins, policy, feedback and protected history stay. Review the exact setup
              files below.
            </p>
            {resetting ? (
              <>
                <div className="cp-reset-preview" aria-label="Reset setup list">
                  {!preview ? (
                    <p role="status">Loading the reset list…</p>
                  ) : (
                    <>
                      <p>
                        Setup files to archive: {preview.files.length}. Linked sources to
                        disconnect: {preview.sources.filter((source) => source.active).length}.
                      </p>
                      <ul>
                        {preview.files.map((file) => (
                          <li key={file.path}>
                            {file.label}: <span className="mono">{file.path}</span> (
                            {file.state === 'watched'
                              ? 'in the client drive'
                              : file.state === 'quarantined'
                                ? 'already archived'
                                : 'already missing'}
                            )
                          </li>
                        ))}
                      </ul>
                      <ul>
                        {preview.sources.map((source) => (
                          <li key={source.id}>
                            {source.name} —{' '}
                            {source.active ? 'will disconnect' : 'already disconnected'}
                          </li>
                        ))}
                      </ul>
                      {preview.interrupted ? (
                        <p>An interrupted reset will resume from its archived copies.</p>
                      ) : null}
                      {preview.blockers.map((blocker) => (
                        <p role="alert" key={blocker}>
                          {blocker}
                        </p>
                      ))}
                    </>
                  )}
                  <button
                    type="button"
                    className="vbtn"
                    disabled={busy || wiping}
                    onClick={() => setPreviewVersion((value) => value + 1)}
                  >
                    Refresh list
                  </button>
                </div>
                <p className="cp-reset-sub">
                  Type <b className="mono">{catalogue.reset_phrase}</b> to confirm:
                </p>
                <input
                  className="cp-reset-input mono"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={catalogue.reset_phrase}
                  aria-label="Reset confirmation phrase"
                />
                <div className="cp-reset-row">
                  <button
                    type="button"
                    className="vbtn danger"
                    disabled={
                      busy ||
                      settling ||
                      wiping ||
                      !preview?.can_reset ||
                      typed !== catalogue.reset_phrase
                    }
                    onClick={doReset}
                  >
                    {busy || wiping ? 'Resetting…' : 'Wipe the record'}
                  </button>
                  <button
                    type="button"
                    className="vbtn"
                    onClick={() => {
                      setResetting(false)
                      setTyped('')
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="vbtn"
                disabled={settling || wiping}
                onClick={() => setResetting(true)}
              >
                {wiping ? 'Reset in progress…' : 'Reset record…'}
              </button>
            )}
            {settling ? (
              <p className="cp-reset-sub" role="status">
                A story action is still settling — reset unlocks when it finishes.
              </p>
            ) : null}
            {outcome ? <p className="cp-reset-outcome">{outcome}</p> : null}
          </div>
        ) : null}
      </aside>
    </>
  )
}

/* ── the runner bar ───────────────────────────────────────────────── */

function RunnerBar({
  run,
  setRun,
  onEnd,
}: {
  run: RunState
  setRun: (f: (r: RunState | null) => RunState | null) => void
  onEnd: () => void
}) {
  const [pace, setPace] = useState<PaceMode>('wait')
  const step = run.story.steps[run.stepIx]
  const last = run.stepIx >= run.story.steps.length - 1
  /* ONE in-flight feed per step, OUTSIDE the effect lifecycle (Codex
   * findings 2026-11-02): StrictMode replays setup→cleanup→setup, and the
   * old `feeding` boolean made the second setup bail while the first
   * closure's `alive` flag was already false — the story stalled on
   * "Feeding" forever. The flight ref survives the replay: the second
   * setup SUBSCRIBES to the same promise instead of re-posting the PDF.
   * The flight's AbortController cancels only the DOWNLOAD phase: an
   * upload POST, once started, may commit server-side regardless of a
   * client abort, so it runs to settlement and is counted
   * (usePendingStoryUploads) — the reset surface stays locked until the
   * count drains, which is what keeps a late upload from landing after a
   * reset. */
  const flight = useRef<{ key: string; controller: AbortController; done: Promise<void> } | null>(
    null
  )

  useEffect(() => {
    if (run.phase !== 'feeding' || !run.setup) return
    const setup = run.setup
    const key = `${run.story.id}:${step.seq}`
    if (!flight.current || flight.current.key !== key) {
      const controller = new AbortController()
      const done = (async () => {
        // Only the download is abortable. Once the upload POST starts the
        // server may commit regardless of a client abort (Codex finding
        // 2026-11-02), so it runs to settlement and is counted by
        // uploadStoryDocument — the reset surface stays locked until the
        // count drains.
        if (run.delivery === 'folder') {
          await deliverStoryToFolder(run.story.id, step.seq, setup.setup_epoch)
        } else {
          const blob = await fetchStoryDocument(
            run.story.id,
            step.seq,
            setup.setup_epoch,
            controller.signal
          )
          if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
          await uploadStoryDocument(blob, step.filename, setup)
        }
        if (consoleDriver) await consoleDriver.onStepLanded(run.story.id, step.seq)
      })()
      const record = { key, controller, done }
      // The settled flight clears ITSELF so a Retry or the next step
      // starts fresh — by record identity, never by key: a StrictMode
      // replay can abort flight A and start flight B for the SAME step,
      // and A's late settlement must not null out B (that left End
      // story with nothing to abort). The offshoot's own rejection is
      // consumed; the effect subscriber below owns error handling.
      done
        .finally(() => {
          if (flight.current === record) flight.current = null
        })
        .catch(() => {})
      flight.current = record
    }
    let alive = true
    flight.current.done.then(
      () => {
        if (alive)
          setRun((r) =>
            r
              ? {
                  ...r,
                  phase: 'landed',
                  note: null,
                  countdown: pace === 'auto' ? AUTO_DWELL_S : null,
                }
              : r
          )
      },
      (e: unknown) => {
        // An abort is the officer ending the story, not a failure.
        if (e instanceof DOMException && e.name === 'AbortError') return
        if (alive)
          setRun((r) =>
            r
              ? {
                  ...r,
                  phase: 'failed',
                  note: e instanceof Error ? e.message : 'The document could not be fed.',
                }
              : r
          )
      }
    )
    return () => {
      alive = false
    }
  }, [run.phase, run.stepIx])

  /* End story cancels whatever is still in flight — and so does unmount. */
  const abortFlight = useCallback(() => {
    flight.current?.controller.abort()
    flight.current = null
  }, [])
  useEffect(() => abortFlight, [abortFlight])

  const next = useCallback(() => {
    setRun((r) => {
      if (!r) return r
      if (r.stepIx >= r.story.steps.length - 1) return { ...r, phase: 'done', countdown: null }
      return { ...r, stepIx: r.stepIx + 1, phase: 'feeding', note: null, countdown: null }
    })
  }, [setRun])

  // The auto pace: tick the countdown on a landed step; 0 feeds the next.
  useEffect(() => {
    if (pace !== 'auto' || run.phase !== 'landed' || run.paused) return
    const t = setInterval(() => {
      setRun((r) => {
        if (!r || r.phase !== 'landed' || r.paused) return r
        const c = (r.countdown ?? AUTO_DWELL_S) - 1
        if (c <= 0) {
          if (r.stepIx >= r.story.steps.length - 1) return { ...r, phase: 'done', countdown: null }
          return { ...r, stepIx: r.stepIx + 1, phase: 'feeding', note: null, countdown: null }
        }
        return { ...r, countdown: c }
      })
    }, 1000)
    return () => clearInterval(t)
  }, [pace, run.phase, run.paused, setRun])

  return (
    <div
      className={'console-bar' + (step.day != null ? ' console-full-story' : '')}
      data-testid="console-bar"
    >
      <span className="cb-story">
        {run.story.name}
        <small>
          {step.day != null ? (
            <>
              {step.chapter} · Day {step.day}
              <br />
            </>
          ) : null}
          step {Math.min(run.stepIx + 1, run.story.steps.length)} of {run.story.steps.length}
        </small>
      </span>
      <span className="cb-narration" data-testid="console-narration">
        {step.day != null && run.phase !== 'done' ? (
          <strong className="cb-document-title">
            {step.title} · {step.filename}
          </strong>
        ) : null}
        {run.phase === 'feeding'
          ? run.delivery === 'folder'
            ? `Waiting for ${step.filename} to be received from the shared folder…`
            : `Feeding ${step.filename} through the normal upload path…`
          : run.phase === 'failed'
            ? (run.note ?? 'The document could not be fed.')
            : run.phase === 'done'
              ? 'Story ended — nothing more to feed. The queues show what the pipeline verified; skipped or failed steps fed nothing.'
              : step.narration}
      </span>
      {pace === 'auto' && run.phase === 'landed' && run.countdown != null ? (
        <span className="cb-count mono">{run.countdown}s</span>
      ) : null}
      <label className="cb-pace">
        pace
        <select
          value={pace}
          onChange={(e) => setPace(e.target.value as PaceMode)}
          aria-label="Pace mode"
        >
          <option value="wait">wait for me</option>
          <option value="auto">auto</option>
          <option value="drive" disabled>
            drive for me (v2)
          </option>
        </select>
      </label>
      {pace === 'auto' && run.phase === 'landed' ? (
        <button
          type="button"
          className="vbtn"
          onClick={() => setRun((r) => (r ? { ...r, paused: !r.paused } : r))}
        >
          {run.paused ? 'Resume' : 'Pause'}
        </button>
      ) : null}
      {run.phase === 'landed' ? (
        <button type="button" className="vbtn pri" onClick={next} data-testid="console-next">
          {last ? 'Finish' : 'Next ›'}
        </button>
      ) : null}
      {run.phase === 'failed' ? (
        <>
          <button
            type="button"
            className="vbtn pri"
            onClick={() => setRun((r) => (r ? { ...r, phase: 'feeding', note: null } : r))}
            data-testid="console-retry"
          >
            Retry
          </button>
          <button type="button" className="vbtn" onClick={next} data-testid="console-next">
            {last ? 'Finish' : 'Skip ›'}
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="cb-end"
        onClick={() => {
          abortFlight()
          onEnd()
        }}
        data-testid="console-end"
      >
        End story
      </button>
    </div>
  )
}

/** Step zero: explicit agreement signing, then observed product readiness. */
function SigningBeat({
  story,
  onReady,
  onEnd,
}: {
  story: Story
  onReady: (setup: ReadySetup, delivery: DeliveryMode) => void
  onEnd: () => void
}) {
  const { user } = useAuth()
  const setupStoryId = story.setup_story_id ?? story.id
  const [delivery, setDelivery] = useState<DeliveryMode>('browser')
  const [status, setStatus] = useState<SigningStatus | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const [busy, setBusy] = useState(false)
  const pending = usePendingStoryUploads() > 0
  const wiping = useResetPending()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    let active = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const deadline = Date.now() + 120000
    const read = async () => {
      try {
        const value = await fetchSigning(setupStoryId, AbortSignal.timeout(5000))
        if (!active) return
        setStatus(value)
        setMessage(null)
        if (value.state === 'registering') {
          if (Date.now() < deadline) timer = setTimeout(read, 3000)
          else setMessage('Registration is still pending. Refresh status to check again.')
        }
      } catch (error) {
        if (active) {
          setStatus(null)
          setMessage(consoleErrorMessage(error))
          if (transientConsoleRead(error) && Date.now() < deadline) timer = setTimeout(read, 1500)
        }
      }
    }
    void read()
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [story.id, setupStoryId, version])
  const sign = async () => {
    if (!status) return
    setBusy(true)
    setMessage(null)
    try {
      const value = await signStory(setupStoryId, status.setup_epoch)
      if (mounted.current) {
        setStatus(value)
        setVersion((n) => n + 1)
      }
    } catch (error) {
      if (mounted.current) {
        setStatus(null)
        setMessage(consoleErrorMessage(error))
        setVersion((n) => n + 1)
      }
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  return (
    <div className="console-bar console-signing" data-testid="console-signing">
      <span className="cb-story">
        {story.name}
        <small>Signing · before documents</small>
      </span>
      <div className="cb-narration">
        <p>
          Sign the depositary agreement and link the client folder. Documents wait until the fund is
          registered.
        </p>
        <p role="status">
          {message ?? status?.message ?? 'Checking the agreement and linked folder…'}
        </p>
        {status?.state === 'ready' && status.folder_delivery_available && delivery === 'folder' ? (
          <p>Each document is placed in the linked folder. Next appears when NOLOS receives it.</p>
        ) : null}
        {user?.role !== 'admin' && status?.state === 'not_started' ? (
          <p>An administrator must sign the agreement.</p>
        ) : null}
      </div>
      {(status?.state === 'not_started' || status?.state === 'retry') && user?.role === 'admin' ? (
        <button
          type="button"
          className="vbtn pri"
          disabled={busy || pending || wiping}
          onClick={sign}
        >
          {status.state === 'retry' ? 'Retry signing' : 'Sign agreement and link folder'}
        </button>
      ) : null}
      {status &&
      isReadySetup(status) &&
      status.folder_delivery_available &&
      user?.role === 'admin' ? (
        <label className="cb-delivery">
          Delivery
          <select
            aria-label="Delivery"
            value={delivery}
            disabled={busy || pending || wiping}
            onChange={(event) => setDelivery(event.target.value as DeliveryMode)}
          >
            <option value="browser">Browser upload</option>
            <option value="folder">Shared folder</option>
          </select>
        </label>
      ) : null}
      {status && isReadySetup(status) ? (
        <button
          type="button"
          className="vbtn pri"
          disabled={busy || pending || wiping}
          onClick={() =>
            onReady(
              status,
              status.folder_delivery_available && user?.role === 'admin' ? delivery : 'browser'
            )
          }
        >
          Start documents
        </button>
      ) : null}
      <button
        type="button"
        className="vbtn"
        disabled={busy || wiping}
        onClick={() => setVersion((n) => n + 1)}
      >
        Refresh status
      </button>
      <button type="button" className="cb-end" onClick={onEnd} data-testid="console-end">
        End story
      </button>
    </div>
  )
}
